// The registration name PATCH can fail after the buyer account is already
// active server-side, permanently losing the name the user typed. Persist the
// pending write so the next authenticated visit can replay it; cleared only
// after a confirmed success.
const STORAGE_KEY = 'BStock::AP::PendingPersonUpdate'

// A record this old belongs to a long-abandoned sign-up; keeping a name and
// phone number in shared browser storage past that is pure liability.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export type PendingPersonUpdate = {
  personId: string
  firstName?: string
  lastName?: string
  phone?: string
  createdAt: string
}

export const getPendingPersonUpdate = (): PendingPersonUpdate | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const record = JSON.parse(raw) as Partial<PendingPersonUpdate> | null
    if (!record || typeof record.personId !== 'string' || !record.personId) {
      return null
    }

    const createdAt = Date.parse(record.createdAt ?? '')
    if (Number.isNaN(createdAt) || Date.now() - createdAt > MAX_AGE_MS) {
      clearPendingPersonUpdate()
      return null
    }

    return record as PendingPersonUpdate
  } catch {
    return null
  }
}

export const setPendingPersonUpdate = (
  record: Omit<PendingPersonUpdate, 'createdAt'>
): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...record, createdAt: new Date().toISOString() })
    )
  } catch {
    // localStorage unavailable (SSR, private mode, quota): replay is
    // best-effort on top of the in-flow retry, never a blocker.
  }
}

export const clearPendingPersonUpdate = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Same best-effort contract as setPendingPersonUpdate.
  }
}
